import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

export interface UseResizablePanelOptions {
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  /** Maximum width as a fraction of viewport width (e.g. 0.6 = 60vw). */
  maxWidthFactor: number;
  /** Pixels added/removed per arrow-key press for keyboard resize. */
  keyboardStep?: number;
  /**
   * Ref to the resizable element. Required for correct drag math when the
   * panel is not flush against the viewport's left edge (e.g. there's a
   * fixed sidebar in front of it). The panel's left offset is read on
   * mousedown so the cursor's `clientX` can be translated into a width
   * relative to the panel's own coordinate space.
   */
  panelRef?: RefObject<HTMLElement | null>;
}

export interface UseResizablePanelReturn {
  width: number;
  minWidth: number;
  /** Current effective max width (depends on live viewport). */
  maxWidth: number;
  isDragging: boolean;
  handleProps: {
    onMouseDown: (event: React.MouseEvent) => void;
    onDoubleClick: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
  };
}

function readStoredWidth(storageKey: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return fallback;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function computeMaxWidth(minWidth: number, maxWidthFactor: number): number {
  const viewport = typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth;
  return Math.max(minWidth, Math.floor(viewport * maxWidthFactor));
}

function clampWidth(value: number, minWidth: number, maxWidthFactor: number): number {
  const max = computeMaxWidth(minWidth, maxWidthFactor);
  if (value < minWidth) return minWidth;
  if (value > max) return max;
  return value;
}

export function useResizablePanel(options: UseResizablePanelOptions): UseResizablePanelReturn {
  const { storageKey, defaultWidth, minWidth, maxWidthFactor, keyboardStep = 16, panelRef } = options;

  const [width, setWidth] = useState<number>(() =>
    clampWidth(readStoredWidth(storageKey, defaultWidth), minWidth, maxWidthFactor),
  );
  const [maxWidth, setMaxWidth] = useState<number>(() => computeMaxWidth(minWidth, maxWidthFactor));
  const [isDragging, setIsDragging] = useState(false);

  const frameScheduledRef = useRef(false);
  const pendingWidthRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const persist = useCallback(
    (value: number) => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(storageKey, String(value));
      } catch {
        // Quota or privacy mode — drop silently; width still works in-memory.
      }
    },
    [storageKey],
  );

  // Re-clamp on viewport resize so a stored 800px panel doesn't outgrow a
  // shrunken browser window. RAF-throttled so a flick of the resize edge
  // doesn't burn React renders, and `persist`/`setWidth` stay outside the
  // updater (StrictMode would otherwise duplicate localStorage writes).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let rafId: number | null = null;
    const handleResize = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const nextMax = computeMaxWidth(minWidth, maxWidthFactor);
        setMaxWidth(prev => (prev === nextMax ? prev : nextMax));
        const current = widthRef.current;
        const clamped = clampWidth(current, minWidth, maxWidthFactor);
        if (clamped !== current) {
          setWidth(clamped);
          persist(clamped);
        }
        // Drop any stale in-flight drag target so handleUp doesn't reapply
        // the pre-resize width.
        if (pendingWidthRef.current !== null) {
          pendingWidthRef.current = clampWidth(
            pendingWidthRef.current,
            minWidth,
            maxWidthFactor,
          );
        }
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [minWidth, maxWidthFactor, persist]);

  const onMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      draggingRef.current = true;
      setIsDragging(true);

      const previousUserSelect = document.body.style.userSelect;
      const previousCursor = document.body.style.cursor;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      // Snapshot the panel's left edge so cursor `clientX` (viewport-relative)
      // becomes a width in the panel's own coordinate space. Without this,
      // pages with a fixed sidebar would jump the panel by the sidebar
      // width on first drag.
      const panelLeft = panelRef?.current?.getBoundingClientRect().left ?? 0;

      const handleMove = (moveEvent: MouseEvent) => {
        if (!draggingRef.current) return;
        const next = clampWidth(moveEvent.clientX - panelLeft, minWidth, maxWidthFactor);
        pendingWidthRef.current = next;
        if (frameScheduledRef.current) return;
        frameScheduledRef.current = true;
        rafIdRef.current = window.requestAnimationFrame(() => {
          frameScheduledRef.current = false;
          rafIdRef.current = null;
          if (!mountedRef.current) return;
          if (pendingWidthRef.current !== null) {
            setWidth(pendingWidthRef.current);
          }
        });
      };

      const handleUp = () => {
        draggingRef.current = false;
        setIsDragging(false);
        document.body.style.userSelect = previousUserSelect;
        document.body.style.cursor = previousCursor;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
        cleanupRef.current = null;
        if (rafIdRef.current !== null) {
          window.cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
          frameScheduledRef.current = false;
        }
        const finalWidth = pendingWidthRef.current;
        if (finalWidth !== null) {
          // Re-clamp against the live viewport in case the window was
          // resized smaller mid-drag.
          const clamped = clampWidth(finalWidth, minWidth, maxWidthFactor);
          setWidth(clamped);
          persist(clamped);
          pendingWidthRef.current = null;
        }
      };

      cleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
        document.body.style.userSelect = previousUserSelect;
        document.body.style.cursor = previousCursor;
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
    },
    [minWidth, maxWidthFactor, persist, panelRef],
  );

  const onDoubleClick = useCallback(() => {
    const reset = clampWidth(defaultWidth, minWidth, maxWidthFactor);
    setWidth(reset);
    persist(reset);
  }, [defaultWidth, minWidth, maxWidthFactor, persist]);

  // ARIA-spec keyboard resize: arrows step, Home → min, End → max.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      let next: number | null = null;
      const max = computeMaxWidth(minWidth, maxWidthFactor);
      switch (event.key) {
        case 'ArrowLeft':
          next = width - keyboardStep;
          break;
        case 'ArrowRight':
          next = width + keyboardStep;
          break;
        case 'Home':
          next = minWidth;
          break;
        case 'End':
          next = max;
          break;
        default:
          return;
      }
      event.preventDefault();
      const clamped = clampWidth(next, minWidth, maxWidthFactor);
      if (clamped !== width) {
        setWidth(clamped);
        persist(clamped);
      }
    },
    [width, minWidth, maxWidthFactor, keyboardStep, persist],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancel any RAF queued during a drag so it can't setWidth post-unmount.
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
        frameScheduledRef.current = false;
      }
      pendingWidthRef.current = null;
      // Detach document listeners and restore body styles when unmounting
      // mid-drag, so we don't leak state into the next page.
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  return {
    width,
    minWidth,
    maxWidth,
    isDragging,
    handleProps: { onMouseDown, onDoubleClick, onKeyDown },
  };
}
